import {
  FilterGroup,
  HeaderConfig,
  Row,
} from 'components/Table/KhulaSupplierTable';
import {IFarmInputInvoice} from 'constants/interfaces';
import moment from 'moment';
import {_NumberWithSpaces} from 'constants/index';
import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Select,
  Tooltip,
} from '@material-ui/core';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import {
  apiGetDepotOrder,
  apiUpdateInputOrderPaymentStatus,
  apiUpdateInputOrderStatus,
} from 'constants/Api';
import {reduxUpdateProformaInvoice} from 'appredux/actions';

export const orderTableHeaders = (isAdmin: boolean): HeaderConfig[] => [
  {
    field: 'invoiceNumber',
    // title: 'Order',
    title: 'Invoice #',
    isSortable: true,
    sortField: 'id',
    isSearchable: true,
    searchField: 'id',
    isRightAligned: true,
  },
  {
    field: 'formattedDate',
    title: 'Date',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  ...(isAdmin
    ? [
        {
          field: 'depotName',
          title: 'Depot',
          isSortable: true,
          isSearchable: true,
          isRightAligned: false,
        },
      ]
    : []),
  {
    field: 'farmerFullName',
    title: 'Farmer',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'formattedDeliveryType',
    title: 'Delivery Type',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'formattedPaymentStatus',
    title: 'Payment',
    isSortable: true,
    isSearchable: true,
    searchField: 'paymentStatus',
    isRightAligned: false,
    maxWidth: 110,
    noWrap: true,
  },
  {
    field: 'invoiceStatusSelect',
    title: 'Order Status',
    isSortable: true,
    sortField: 'currOrderStatusDescription',
    isSearchable: true,
    searchField: 'currOrderStatusDescription',
    isRightAligned: false,
    maxWidth: 150,
  },
  {
    field: 'invoiceType',
    title: 'Invoice Type',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'formattedTotal',
    title: 'Total (R)',
    sortField: 'total',
    isSortable: true,
    isSearchable: false,
    isRightAligned: true,
    noWrap: true,
  },
  {
    field: 'actions',
    title: '',
    isSortable: false,
    isSearchable: false,
    isRightAligned: true,
  },
];

export const orderTableRows = (
  canWriteOrders: boolean,
  canManageOrderPayments: boolean,
  orders: IFarmInputInvoice[],
  onSelect: (order: IFarmInputInvoice) => any,
): Row[] =>
  orders.map((order) => {
    const rowId = String(order.id);

    return {
      id: rowId,
      invoiceNumber: order.invoiceNumber,
      formattedDate: moment(parseInt(order.insertedDate, 10)).format(
        'YYYY-MM-DD',
      ),
      depotName: order.depot?.name || '',
      farmerFullName: order.farmer?.name + ' ' + order.farmer?.surname,
      currOrderStatusDescription: order.currOrderStatus.description,
      formattedDeliveryType: getFormattedDeliveryType(order),
      paymentStatus: order.isPaid ? 'Paid' : 'Unpaid',
      formattedPaymentStatus: canManageOrderPayments ? (
        <PaymentStatusSelect order={order} />
      ) : order.isPaid ? (
        'Paid'
      ) : (
        'Unpaid'
      ),
      invoiceStatusSelect: canWriteOrders ? (
        <OrderStatusSelect order={order} disabled={false} />
      ) : (
        order.currOrderStatus.description
      ),
      invoiceType: getInvoiceTypeLabel(order),
      formattedTotal: 'R ' + _NumberWithSpaces(order.total),
      actions: <OrderRowIcons onSelect={() => onSelect(order)} />,
    };
  });

function getFormattedDeliveryType(order: IFarmInputInvoice) {
  const orderLogisticsTypes = order.orders.map((o) => o.logisticsTypeName);
  if (orderLogisticsTypes.includes('Supplier Custom')) return 'Custom';
  if (orderLogisticsTypes.includes('Courier Guy Delivery'))
    return 'Courier Guy Delivery';
  if (orderLogisticsTypes.includes('Free Delivery')) return 'Delivery';
  if (orderLogisticsTypes.includes('Pick Up')) return 'Collection';
  return order.deliveryType;
}

function getInvoiceTypeLabel(
  order: IFarmInputInvoice,
): 'Proforma' | 'Tax Invoice' {
  return order.documentUrl ? 'Tax Invoice' : 'Proforma';
}

function OrderRowIcons(props: {onSelect: () => any}) {
  return (
    <Box display="flex" flexDirection="row">
      <Tooltip title="View order" aria-label="View order">
        <IconButton
          aria-label="view order"
          size="small"
          onClick={props.onSelect}
        >
          <ChatBubbleOutlineIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

const PaymentStatusSelect = (props: {order: IFarmInputInvoice}) => {
  const [isPaid, setIsPaid] = React.useState(props.order.isPaid);
  const [tempIsPaid, setTempIsPaid] = React.useState(props.order.isPaid);
  const [open, setOpen] = React.useState(false);

  const handleChange = (event: React.ChangeEvent<{value: unknown}>) => {
    setTempIsPaid(event.target.value === '1');
    setOpen(true);
  };

  const handleAccept = async () => {
    try {
      await apiUpdateInputOrderPaymentStatus(props.order.id, tempIsPaid);
      const updatedOrder = await apiGetDepotOrder(props.order.id);
      reduxUpdateProformaInvoice(updatedOrder);
      setIsPaid(tempIsPaid);
      setOpen(false);
    } catch (error) {
      alert('Error updating order payment status');
      console.error(error);
      setOpen(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <div className="order-payment-select">
      <Select native value={isPaid ? '1' : '0'} onChange={handleChange}>
        <option value="0">Unpaid</option>
        <option value="1">Paid</option>
      </Select>
      <Dialog
        className="invoice-confirmation-dialog"
        disableEscapeKeyDown
        open={open}
        onClose={handleCancel}
      >
        <DialogTitle>
          Update payment status: {props.order.invoiceNumber}
        </DialogTitle>
        <DialogContent>
          Are you sure you want to update the payment status?
        </DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className="accept-button"
            onClick={handleAccept}
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const OrderStatusSelect = (props: {
  order: IFarmInputInvoice;
  disabled: boolean;
}) => {
  const {order, disabled} = props;
  const orderStatuses = order.orderStatus;
  const firstStatus =
    orderStatuses.find((item) => item.index === 0) || orderStatuses[0];
  const currentStatus =
    orderStatuses.find((item) => item.status === 'IN_PROGRESS') ||
    orderStatuses[0];

  const [orderStatus, setOrderStatus] = React.useState(
    currentStatus || firstStatus,
  );
  const [tempOrderStatus, setTempOrderStatus] = React.useState(orderStatus);
  const [open, setOpen] = React.useState(false);

  const handleChange = (event: React.ChangeEvent<{value: unknown}>) => {
    const selectedIndex = parseInt(event.target.value as string, 10);
    const selectedStatus =
      orderStatuses.find((s) => s.index === selectedIndex) || orderStatus;
    setTempOrderStatus(selectedStatus);
    setOpen(true);
  };

  const handleAccept = async () => {
    const prevStatus = orderStatus;
    try {
      await apiUpdateInputOrderStatus(order.id, tempOrderStatus.index);
      const updatedOrder = await apiGetDepotOrder(order.id);
      reduxUpdateProformaInvoice(updatedOrder);
      setOrderStatus(tempOrderStatus);
      setOpen(false);
    } catch (error) {
      alert('Error updating order status');
      console.error(error);
      setOrderStatus(prevStatus);
      setOpen(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <div className="order-status-select">
      <Select
        native
        value={orderStatus.index}
        onChange={handleChange}
        disabled={disabled}
      >
        {orderStatuses.map((status) => (
          <option key={status.index} value={status.index}>
            {status.description}
          </option>
        ))}
      </Select>
      <Dialog
        className="invoice-confirmation-dialog"
        disableEscapeKeyDown
        open={open}
        onClose={handleCancel}
      >
        <DialogTitle>Update order status: {order.invoiceNumber}</DialogTitle>
        <DialogContent>
          Are you sure you want to update the order status?
        </DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className="accept-button"
            onClick={handleAccept}
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const deliveryTypeFilterGroup: FilterGroup = {
  name: 'Delivery Type',
  filters: [
    {field: 'formattedDeliveryType', value: 'Supplier Custom', title: 'Custom'},
    {
      field: 'formattedDeliveryType',
      value: 'Courier Guy Delivery',
      title: 'Courier Guy Delivery',
    },
    {field: 'formattedDeliveryType', value: 'Collection', title: 'Collection'},
    {field: 'formattedDeliveryType', value: 'Delivery', title: 'Delivery'},
  ],
};

const invoiceTypeFilterGroup: FilterGroup = {
  name: 'Invoice Type',
  filters: [
    {
      field: 'invoiceType',
      value: 'Proforma',
      title: 'Proforma',
    },
    {
      field: 'invoiceType',
      value: 'Tax Invoice',
      title: 'Tax Invoice',
    },
  ],
};

const paymentStatusFilterGroup: FilterGroup = {
  name: 'Payment Status',
  filters: [
    {field: 'paymentStatus', value: 'Paid', title: 'Paid'},
    {field: 'paymentStatus', value: 'Unpaid', title: 'Unpaid'},
  ],
};

export const orderTableFilters = [
  deliveryTypeFilterGroup,
  paymentStatusFilterGroup,
  invoiceTypeFilterGroup,
];
